import { useCallback, useEffect, useState, type FormEvent } from 'react';

import {
  listNetworkInvestors, listEvaluations, tagInvestor, getEligibility,
  type NetworkInvestor, type NetworkEvaluation, type EligibilityReport, type EvaluationInput,
} from '../../lib/personas';
import { listOpportunities, type AdminOpportunity } from '../../lib/admin';
import { ApiError } from '../../lib/apiClient';
import { formatDateShort, formatDateTime } from '../../lib/format';
import './AdminPages.css';
import './AdminNetwork.css';

/**
 * Rule 506(b) private network — the tagging screen.
 *
 * TAGGING IS A COMPLIANCE ACT, NOT A PERMISSION TOGGLE. The four fields below are the
 * record the exemption rests on: if the SEC asks how the issuer came to have a
 * substantive relationship with a given investor, this is the answer. The engine rejects
 * anything under 40 characters per field, and the form says so up front rather than
 * letting someone type "ok" four times and discover the refusal on submit.
 *
 * THE ONE-WAY DOOR is the other half of this screen. Eligibility requires a relationship
 * that PREDATES the offering, and offering_start_date is stamped the moment a raise
 * opens — so an admin who opens a private raise before tagging its intended investors has
 * excluded them from it permanently. No later evaluation can undo that. The eligibility
 * panel therefore works on DRAFTS too, projecting "if you opened this now", because
 * before opening is the only moment the warning can still change anything.
 */

const MIN_LENGTH = 40;

const FIELDS: { key: keyof EvaluationInput; label: string; hint: string }[] = [
  {
    key: 'financialCircumstances',
    label: 'Financial circumstances',
    hint: 'What you reviewed and what it showed, income, net worth, liquidity, and how it was evidenced.',
  },
  {
    key: 'investmentSophistication',
    label: 'Investment sophistication',
    hint: 'Their experience with private placements and whether they understand illiquidity and total-loss risk.',
  },
  {
    key: 'riskTolerance',
    label: 'Risk tolerance',
    hint: 'What they have said about loss of principal, and how this sits against the rest of their portfolio.',
  },
  {
    key: 'investmentGoals',
    label: 'Investment goals',
    hint: 'What they are trying to achieve, and over what horizon.',
  },
];

const EMPTY: EvaluationInput = {
  financialCircumstances: '',
  investmentSophistication: '',
  riskTolerance: '',
  investmentGoals: '',
};

// ── Tagging form ────────────────────────────────────────────────────────────

function TagForm({
  investor, onDone, onCancel,
}: {
  investor: NetworkInvestor;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [values, setValues] = useState<EvaluationInput>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [badField, setBadField] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setBadField(null);
    try {
      await tagInvestor(investor.userId, values);
      onDone();
    } catch (err: unknown) {
      // The engine names the offending field as `evaluation_too_short:<field>`, so the
      // form can point at the box rather than saying "invalid" and leaving them to guess.
      if (err instanceof ApiError && err.message.startsWith('evaluation_too_short:')) {
        const field = err.message.split(':')[1];
        setBadField(field);
        setError(`"${FIELDS.find((f) => f.key === field)?.label ?? field}" needs at least ${MIN_LENGTH} characters of real assessment.`);
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not record the evaluation.');
      }
      setBusy(false);
    }
  }

  return (
    <form className="net-form" onSubmit={submit}>
      <p className="net-form__intro">
        This is a compliance record, not a switch. Write what you actually established
        about {investor.email} and how, it is what a 506(b) exemption would rest on if
        anyone asks. Each field needs at least {MIN_LENGTH} characters.
      </p>

      {FIELDS.map((field) => {
        const value = values[field.key];
        const short = value.trim().length > 0 && value.trim().length < MIN_LENGTH;
        return (
          <div className="field" key={field.key}>
            <label className="field__label" htmlFor={`f-${field.key}`}>
              {field.label}
            </label>
            <p className="net-form__hint">{field.hint}</p>
            <textarea
              id={`f-${field.key}`}
              className={
                badField === field.key ? 'field__input net-form__area is-bad' : 'field__input net-form__area'
              }
              rows={3}
              value={value}
              onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
            />
            <span className={short ? 'net-form__count is-short' : 'net-form__count'}>
              {value.trim().length} / {MIN_LENGTH} minimum
            </span>
          </div>
        );
      })}

      {error && (
        <div className="alert alert--error" role="alert">
          {error}
        </div>
      )}

      <div className="net-form__actions">
        <button type="submit" className="button button--primary" disabled={busy}>
          {busy ? 'Recording…' : 'Record evaluation'}
        </button>
        <button type="button" className="button button--ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>

      <p className="net-form__note">
        Evaluations are appended, never overwritten, the history is part of the record.
        The relationship date stays the EARLIEST evaluation, so a later one cannot move it
        forward and change which raises this investor qualifies for.
      </p>
    </form>
  );
}

// ── Existing evaluations ────────────────────────────────────────────────────

function EvaluationHistory({ investorId }: { investorId: string }) {
  const [rows, setRows] = useState<NetworkEvaluation[] | null>(null);

  useEffect(() => {
    listEvaluations(investorId).then(setRows).catch(() => setRows([]));
  }, [investorId]);

  if (!rows) return <p className="net-state">Loading evaluations…</p>;
  if (rows.length === 0) return <p className="net-state">No evaluations on record.</p>;

  return (
    <div className="net-history">
      {rows.map((row, index) => (
        <article key={row.id} className="net-history__item">
          <header className="net-history__head">
            <span className="mono">{formatDateTime(row.evaluatedAt)}</span>
            <span className="net-history__by">by {row.evaluatedBy.email}</span>
            {index === rows.length - 1 && (
              <span className="badge badge--network">Relationship established</span>
            )}
          </header>
          <dl className="net-history__fields">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <dt>{f.label}</dt>
                <dd>{row[f.key]}</dd>
              </div>
            ))}
          </dl>
        </article>
      ))}
    </div>
  );
}

// ── Eligibility panel: the one-way door ─────────────────────────────────────

export function EligibilityPanel({ offeringId }: { offeringId: string }) {
  const [report, setReport] = useState<EligibilityReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getEligibility(offeringId)
      .then((r) => !cancelled && setReport(r))
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load eligibility.');
      });
    return () => { cancelled = true; };
  }, [offeringId]);

  if (error) return <div className="alert alert--error">{error}</div>;
  if (!report) return <p className="net-state">Checking eligibility…</p>;

  if (report.offering.offeringType !== '506b') {
    return (
      <p className="net-state">
        This is a {report.offering.offeringType} offering, it is publicly solicitable, so
        network tagging does not gate who can see it.
      </p>
    );
  }

  const lockedOut = report.taggedTooLate.length;
  const untagged = report.notTagged.length;

  return (
    <div className="net-elig">
      <div className="net-elig__counts">
        <div className="net-elig__count net-elig__count--ok">
          <span className="net-elig__n mono">{report.eligible.length}</span>
          <span className="net-elig__label">
            {report.projected ? 'would be eligible' : 'can see this deal'}
          </span>
        </div>
        <div className={untagged > 0 ? 'net-elig__count net-elig__count--warn' : 'net-elig__count'}>
          <span className="net-elig__n mono">{untagged}</span>
          <span className="net-elig__label">approved but never evaluated</span>
        </div>
        <div className={lockedOut > 0 ? 'net-elig__count net-elig__count--bad' : 'net-elig__count'}>
          <span className="net-elig__n mono">{lockedOut}</span>
          <span className="net-elig__label">tagged too late, permanently excluded</span>
        </div>
      </div>

      {report.projected ? (
        // `projected` means the offering has no start date — which happens in two quite
        // different situations, and telling a user "this raise has not opened" about a
        // raise that IS open would be simply false. Legacy rows predating the
        // offering_start_date column are open but undated; for those the honest statement
        // is that the start is unrecorded, not that it has not happened.
        <p className="net-elig__banner net-elig__banner--warn">
          {report.offering.status === 'open' ? (
            <>
              <strong>This raise is open but has no recorded start date.</strong> Eligibility
              is being measured against now, which is the best available answer, but until
              a start date is set, no investor can actually pass the 506(b) gate for it,
              because the gate requires one.
            </>
          ) : (
            <>
              <strong>This raise has not opened.</strong> These are the numbers you would
              get by opening it now. Anyone in the middle column can still be tagged
              first, once you open, they are locked out of this deal for good.
            </>
          )}
        </p>
      ) : (
        <p className="net-elig__banner">
          Offering started {formatDateShort(report.offering.offeringStartDate)}. Only
          relationships established before that date count for this raise.
        </p>
      )}

      {lockedOut > 0 && (
        <div className="net-elig__group">
          <h4>Permanently excluded from this raise</h4>
          <p className="net-elig__why">
            Tagged after this offering began. Under Rule 506(b) the relationship must
            already have existed when the offering started, so no further evaluation can
            make these investors eligible for <em>this</em> deal. They remain eligible for
            future raises opened after their tag date.
          </p>
          <ul>
            {report.taggedTooLate.map((i) => (
              <li key={i.userId}>
                <span className="mono">{i.email}</span>
                <span className="net-elig__date">tagged {formatDateShort(i.taggedAt)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {untagged > 0 && (
        <div className="net-elig__group">
          <h4>Approved investors with no evaluation</h4>
          <ul>
            {report.notTagged.map((i) => (
              <li key={i.userId}>
                <span className="mono">{i.email}</span>
                {i.isTestPersona && <span className="badge badge--muted">test persona</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {report.eligible.length > 0 && (
        <div className="net-elig__group">
          <h4>{report.projected ? 'Would be eligible' : 'Can see this deal'}</h4>
          <ul>
            {report.eligible.map((i) => (
              <li key={i.userId}>
                <span className="mono">{i.email}</span>
                <span className="net-elig__date">tagged {formatDateShort(i.taggedAt)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── The screen ──────────────────────────────────────────────────────────────

export function AdminNetwork() {
  const [investors, setInvestors] = useState<NetworkInvestor[]>([]);
  const [opportunities, setOpportunities] = useState<AdminOpportunity[]>([]);
  const [selectedOffering, setSelectedOffering] = useState<string>('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [tagging, setTagging] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([listNetworkInvestors(), listOpportunities()])
      .then(([rows, opps]) => {
        setInvestors(rows);
        setOpportunities(opps.filter((o) => o.offeringType === '506b'));
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not load the network.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const tagged = investors.filter((b) => b.networkTagged).length;

  return (
    <div className="admin-page">
      <header className="admin-page__header">
        <h1>506(b) private network</h1>
        <p className="admin-page__lede">
          A Rule 506(b) offering may only be shown to investors the issuer already had a
          substantive relationship with <em>before</em> the offering began. Tagging records
          that relationship, it is a compliance record, not an access switch.
        </p>
      </header>

      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {/* The one-way door, front and centre. */}
      <section className="net-panel">
        <h2 className="net-panel__title">Check a private raise</h2>
        <p className="net-panel__lede">
          See who can reach a 506(b) deal, and, before you open it, who would be shut out
          permanently.
        </p>
        {opportunities.length === 0 ? (
          <p className="net-state">No 506(b) offerings exist yet.</p>
        ) : (
          <>
            <div className="field">
              <label className="field__label" htmlFor="net-offering">Offering</label>
              <select
                id="net-offering"
                className="field__input"
                value={selectedOffering}
                onChange={(e) => setSelectedOffering(e.target.value)}
              >
                <option value="">Select a 506(b) offering…</option>
                {opportunities.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.title}, {o.status}
                    {o.offeringStartDate ? '' : ' (not opened)'}
                  </option>
                ))}
              </select>
            </div>
            {selectedOffering && <EligibilityPanel offeringId={selectedOffering} />}
          </>
        )}
      </section>

      <section className="net-panel">
        <h2 className="net-panel__title">
          Approved investors <span className="net-panel__count mono">{tagged} of {investors.length} tagged</span>
        </h2>

        {loading && <p className="net-state">Loading…</p>}

        <div className="net-list">
          {investors.map((investor) => (
            <article key={investor.userId} className="net-row">
              <div className="net-row__main">
                <span className="net-row__email mono">{investor.email}</span>
                {investor.name && <span className="net-row__name">{investor.name}</span>}
                <div className="net-row__badges">
                  {investor.networkTagged ? (
                    <span className="badge badge--network">
                      Network member since {formatDateShort(investor.networkTaggedAt)}
                    </span>
                  ) : (
                    <span className="badge badge--muted">Not evaluated, 506(c) only</span>
                  )}
                  {investor.evaluationCount > 1 && (
                    <span className="badge badge--muted">{investor.evaluationCount} evaluations</span>
                  )}
                  {!investor.approvedByAdmin && (
                    <span className="badge badge--pending">Investor not approved</span>
                  )}
                </div>
              </div>

              <div className="net-row__actions">
                <button
                  type="button"
                  className="button button--ghost adm-btn-sm"
                  onClick={() => setExpanded(expanded === investor.userId ? null : investor.userId)}
                >
                  {expanded === investor.userId ? 'Hide history' : 'History'}
                </button>
                <button
                  type="button"
                  className="button button--secondary adm-btn-sm"
                  onClick={() => setTagging(tagging === investor.userId ? null : investor.userId)}
                >
                  {investor.networkTagged ? 'Re-evaluate' : 'Evaluate & tag'}
                </button>
              </div>

              {expanded === investor.userId && (
                <div className="net-row__panel">
                  <EvaluationHistory investorId={investor.userId} />
                </div>
              )}

              {tagging === investor.userId && (
                <div className="net-row__panel">
                  <TagForm
                    investor={investor}
                    onDone={() => { setTagging(null); load(); }}
                    onCancel={() => setTagging(null)}
                  />
                </div>
              )}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
